import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { StorageService, STORAGE_KEYS } from './storage.service';

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────

export interface FcmRegisterDto {
  token: string;
  deviceFingerprint?: string;
  deviceType: 'web' | 'android' | 'ios';
  deviceInfo?: string;
}

export interface FcmUnregisterDto {
  token: string;
}

export interface FcmNotifyDto {
  userId: string;
  title: string;
  body: string;
  data?: Record<string, string>;
  imageUrl?: string;
}

export interface FcmNotifyAnonymousDto {
  deviceFingerprint: string;
  title: string;
  body: string;
  data?: Record<string, string>;
  imageUrl?: string;
}

export interface FcmBroadcastDto {
  title: string;
  body: string;
  data?: Record<string, string>;
  imageUrl?: string;
  topic?: string;
}

export interface FcmTokenStats {
  totalTokens: number;
  deviceTypes: {
    web: number;
    android: number;
    ios: number;
  };
  tokens: Array<{
    id: string;
    deviceType: string;
    deviceInfo?: string;
    createdAt: string;
    lastUsedAt?: string;
  }>;
}

export interface FcmResponse {
  success: boolean;
  message: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root'
})
export class FcmService {
  private readonly http = inject(HttpClient);
  private readonly storageService = inject(StorageService);
  private readonly baseUrl = `${environment.apiUrl}/fcm`;
  
  private registeredTokenSubject = new BehaviorSubject<string | null>(null);
  
  /** Observable that emits the currently registered token */
  registeredToken$ = this.registeredTokenSubject.asObservable();

  /**
   * Get device information for registration
   */
  private getDeviceInfo(): string {
    const ua = navigator.userAgent;
    const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/);
    const browser = browserMatch ? browserMatch[0] : 'Unknown Browser';
    return `${browser} on ${navigator.platform}`;
  }

  /**
   * Generate a simple device fingerprint for anonymous users
   */
  private generateDeviceFingerprint(): string {
    const stored = this.storageService.getRaw('device', STORAGE_KEYS.device.fingerprint);
    if (stored) return stored;
    
    const fingerprint = `web_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    this.storageService.setRaw('device', STORAGE_KEYS.device.fingerprint, fingerprint);
    return fingerprint;
  }

  /**
   * Register FCM token with the backend
   * Works for both authenticated and anonymous users
   * 
   * @param token - The FCM token from Firebase
   * @param deviceFingerprint - Optional fingerprint for anonymous users
   */
  registerToken(token: string, deviceFingerprint?: string): Observable<FcmResponse> {
    const dto: FcmRegisterDto = {
      token,
      deviceFingerprint: deviceFingerprint || this.generateDeviceFingerprint(),
      deviceType: 'web',
      deviceInfo: this.getDeviceInfo()
    };

    return this.http.post<FcmResponse>(`${this.baseUrl}/register`, dto).pipe(
      tap(response => {
        if (response.success) {
          this.registeredTokenSubject.next(token);
          console.log('✅ FCM token registered with backend');
        }
      }),
      catchError(error => {
        console.error('❌ Failed to register FCM token:', error);
        return of({ success: false, message: error.message || 'Registration failed' });
      })
    );
  }

  /**
   * Unregister FCM token from the backend
   * Call this when user logs out or disables notifications
   * 
   * @param token - The FCM token to unregister
   */
  unregisterToken(token: string): Observable<FcmResponse> {
    const dto: FcmUnregisterDto = { token };

    return this.http.post<FcmResponse>(`${this.baseUrl}/unregister`, dto).pipe(
      tap(response => {
        if (response.success) {
          this.registeredTokenSubject.next(null);
          console.log('✅ FCM token unregistered from backend');
        }
      }),
      catchError(error => {
        console.error('❌ Failed to unregister FCM token:', error);
        return of({ success: false, message: error.message || 'Unregistration failed' });
      })
    );
  }

  /**
   * Unregister the currently stored token
   */
  unregisterCurrentToken(): Observable<FcmResponse> {
    const token = this.registeredTokenSubject.value;
    if (!token) {
      return of({ success: false, message: 'No token registered' });
    }
    return this.unregisterToken(token);
  }

  /**
   * Send notification to a specific user (Admin/Manager only)
   * 
   * @param dto - Notification details including userId
   */
  notifyUser(dto: FcmNotifyDto): Observable<FcmResponse> {
    return this.http.post<FcmResponse>(`${this.baseUrl}/notify`, dto).pipe(
      tap(() => console.log(`📤 Notification sent to user: ${dto.userId}`)),
      catchError(error => {
        console.error('❌ Failed to send notification:', error);
        return of({ success: false, message: error.message || 'Failed to send notification' });
      })
    );
  }

  /**
   * Send notification to an anonymous user by device fingerprint (Admin/Manager only)
   * 
   * @param dto - Notification details including deviceFingerprint
   */
  notifyAnonymous(dto: FcmNotifyAnonymousDto): Observable<FcmResponse> {
    return this.http.post<FcmResponse>(`${this.baseUrl}/notify-anonymous`, dto).pipe(
      tap(() => console.log(`📤 Notification sent to anonymous device: ${dto.deviceFingerprint}`)),
      catchError(error => {
        console.error('❌ Failed to send anonymous notification:', error);
        return of({ success: false, message: error.message || 'Failed to send notification' });
      })
    );
  }

  /**
   * Broadcast notification to all users (Admin only)
   * 
   * @param dto - Broadcast notification details
   */
  broadcast(dto: FcmBroadcastDto): Observable<FcmResponse> {
    return this.http.post<FcmResponse>(`${this.baseUrl}/broadcast`, dto).pipe(
      tap(() => console.log('📢 Broadcast notification sent')),
      catchError(error => {
        console.error('❌ Failed to broadcast notification:', error);
        return of({ success: false, message: error.message || 'Failed to broadcast' });
      })
    );
  }

  /**
   * Get FCM token statistics for the current user (requires JWT)
   */
  getStats(): Observable<FcmTokenStats | null> {
    return this.http.get<FcmTokenStats>(`${this.baseUrl}/stats`).pipe(
      catchError(error => {
        console.error('❌ Failed to get FCM stats:', error);
        return of(null);
      })
    );
  }

  /**
   * Check if a token is currently registered
   */
  isTokenRegistered(): boolean {
    return this.registeredTokenSubject.value !== null;
  }

  /**
   * Get the device fingerprint (useful for anonymous tracking)
   */
  getDeviceFingerprint(): string {
    return this.generateDeviceFingerprint();
  }
}
